import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Phone, Mail, Building2, Users, HeartHandshake } from 'lucide-react';
import SEO from '../components/SEO';

const GroupInsurance = () => {
  return (
    <div className="group-insurance-page">
      <SEO 
        title="Group Insurance Solutions"
        description="Comprehensive group health insurance for businesses and organizations. Competitive rates, multiple carrier options, and personalized service for employer-sponsored coverage in Michigan."
        keywords="group health insurance, employer health insurance, business health insurance, group coverage, employee benefits, small business insurance, group insurance Michigan"
        path="/group-insurance"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-teal-50 to-blue-50 py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <img 
                src="/images/small-business.jpg" 
                alt="Small Business Health Insurance" 
                className="rounded-lg shadow-xl w-full object-cover"
              />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Small Business Health Insurance</h1>
              <p className="text-xl text-gray-700 mb-6">
                Comprehensive group coverage options for your employees
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Offer Group Insurance */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Why Offer Group Health Insurance?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="border-2 border-teal-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-teal-600" />
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Attract & Retain Talent</h3>
                <p className="text-gray-600">Competitive benefits packages help you recruit and keep the best employees.</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-teal-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <HeartHandshake className="w-12 h-12 mx-auto mb-4 text-teal-600" />
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Tax Advantages</h3>
                <p className="text-gray-600">Employer contributions are typically tax-deductible business expenses.</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-teal-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <Building2 className="w-12 h-12 mx-auto mb-4 text-teal-600" />
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Healthier Workforce</h3>
                <p className="text-gray-600">Access to healthcare leads to healthier, more productive employees.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">What We Offer</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 border-teal-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-teal-600 mb-4">Group Health Insurance</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>Medical, dental, and vision coverage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>Multiple plan options and tiers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>PPO, HMO, and HDHP plans available</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>Flexible contribution structures</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4">Voluntary Benefits</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">✓</span>
                    <span>Life insurance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">✓</span>
                    <span>Disability insurance (short-term & long-term)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">✓</span>
                    <span>Critical illness coverage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">✓</span>
                    <span>Accident insurance</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Carrier Logos Section with Scrolling Animation */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">We Work With Leading Insurance Carriers</h2>
          
          {/* Scrolling Logos Carousel */}
          <div className="relative overflow-hidden bg-gray-50 rounded-lg shadow-lg py-8">
            <div className="flex animate-scroll-logos gap-12 items-center px-6">
              {/* First set */}
              <img src="/images/carrier-misc.png" alt="Trinity" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-bcbs.png" alt="Blue Cross Blue Shield" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-cigna.png" alt="Cigna" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-humana.webp" alt="Humana" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-aarp-uhc.png" alt="UnitedHealthcare" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-aetna.png" alt="Aetna" className="h-16 object-contain flex-shrink-0" style={{filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(160deg) brightness(91%) contrast(97%)'}} />
              <img src="/images/logo-hap.webp" alt="HAP" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-priority-health.png" alt="Priority Health" className="h-16 object-contain flex-shrink-0" style={{filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(160deg) brightness(91%) contrast(97%)'}} />
              <img src="/images/mclaren.png" alt="McLaren" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-mutual-of-omaha.png" alt="Mutual of Omaha" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-eyemed.png" alt="EyeMed" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-vsp.png" alt="VSP" className="h-16 object-contain flex-shrink-0" />
              {/* Duplicate for seamless loop */}
              <img src="/images/carrier-misc.png" alt="Trinity" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-bcbs.png" alt="Blue Cross Blue Shield" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-cigna.png" alt="Cigna" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-humana.webp" alt="Humana" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-aarp-uhc.png" alt="UnitedHealthcare" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-aetna.png" alt="Aetna" className="h-16 object-contain flex-shrink-0" style={{filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(160deg) brightness(91%) contrast(97%)'}} />
              <img src="/images/logo-hap.webp" alt="HAP" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-priority-health.png" alt="Priority Health" className="h-16 object-contain flex-shrink-0" style={{filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(160deg) brightness(91%) contrast(97%)'}} />
              <img src="/images/mclaren.png" alt="McLaren" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-mutual-of-omaha.png" alt="Mutual of Omaha" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-eyemed.png" alt="EyeMed" className="h-16 object-contain flex-shrink-0" />
              <img src="/images/logo-vsp.png" alt="VSP" className="h-16 object-contain flex-shrink-0" />
            </div>
          </div>
        </div>
      </section>

      {/* How We Help Small Businesses - 4 Card Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">How We Help Small Businesses</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Card 1: Expert Guidance */}
            <Card className="border-2 border-teal-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Building2 className="w-12 h-12 mb-4 text-teal-600" />
                <h3 className="text-2xl font-bold text-teal-600 mb-4">Expert Guidance</h3>
                <p className="text-gray-700 text-lg">
                  Navigate the complex world of group insurance with personalized support. We help you understand your options and choose the right coverage for your team.
                </p>
              </CardContent>
            </Card>

            {/* Card 2: Competitive Rates */}
            <Card className="border-2 border-teal-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Users className="w-12 h-12 mb-4 text-teal-600" />
                <h3 className="text-2xl font-bold text-teal-600 mb-4">Competitive Rates</h3>
                <p className="text-gray-700 text-lg">
                  Access multiple carriers and plan options to find coverage that fits your budget. We shop the market to ensure you get the best value.
                </p>
              </CardContent>
            </Card>

            {/* Card 3: Simplified Administration */}
            <Card className="border-2 border-teal-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <HeartHandshake className="w-12 h-12 mb-4 text-teal-600" />
                <h3 className="text-2xl font-bold text-teal-600 mb-4">Simplified Administration</h3>
                <p className="text-gray-700 text-lg">
                  From enrollment to renewals, we handle the details so you can focus on running your business. Ongoing support when you need it.
                </p>
              </CardContent>
            </Card>

            {/* Card 4: Employee Education */}
            <Card className="border-2 border-teal-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Building2 className="w-12 h-12 mb-4 text-teal-600" />
                <h3 className="text-2xl font-bold text-teal-600 mb-4">Employee Education</h3>
                <p className="text-gray-700 text-lg">
                  Help your team understand their benefits with clear explanations and enrollment assistance. We make sure everyone gets the most from their coverage.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Ready to Offer Health Benefits - Green Contact Section */}
      <section className="py-16 px-4 bg-green-600">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-white mb-4">Ready to Offer Health Benefits to Your Team- Contact us today</h2>
            <p className="text-2xl text-white">
              Let's find the right group insurance solution for your business
            </p>
          </div>
          
          {/* Contact Form */}
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-center mb-6 text-gray-900">Request a Quote</h3>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Button asChild className="bg-teal-600 hover:bg-teal-700 text-white text-xl py-7 px-10 w-full sm:w-auto">
                    <a href="https://calendly.com/llplans/30min" target="_blank" rel="noopener noreferrer">
                      Schedule a Meeting
                    </a>
                  </Button>
                  <Button asChild className="bg-teal-600 hover:bg-teal-700 text-white text-xl py-7 px-10 w-full sm:w-auto">
                    <a href="tel:734.209.6712">
                      <Phone className="mr-2" size={24} />
                      Call 734.209.6712
                    </a>
                  </Button>
                </div>
                <div className="text-center text-gray-700">
                  <Mail className="inline mr-2" size={20} />
                  <span>Email: <a href="mailto:Paulette@llplans.com" className="text-teal-600 hover:text-teal-700 font-semibold">Paulette@llplans.com</a></span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default GroupInsurance;